import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ContextHelpButton from './ContextHelpButton';

/**
 * Legacy projects may predate Client Brain links. Ask the owner to identify
 * their source project once; never guess an identity match from project names.
 */
export default function ClientWorkspaceRecovery({ api, clientId, workspace, onRecovered }) {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const defaults = workspace?.workspace_defaults || {};
  const hasV3 = Array.isArray(defaults.design_v3_templates) && defaults.design_v3_templates.length > 0;
  const hasTov = !!(defaults.tov && Object.keys(defaults.tov).length);
  const hasBrand = !!(defaults.brand_kit && Object.keys(defaults.brand_kit).length);
  const needsRecovery = !hasV3 || !hasTov || !hasBrand;

  useEffect(() => {
    setSourceId('');
    setShowPicker(false);
    setProjects([]);
  }, [clientId]);

  useEffect(() => {
    if (!showPicker || !clientId) return;
    let cancelled = false;
    setLoading(true);
    api.get('/projects')
      .then(({ data }) => {
        if (cancelled) return;
        const available = (Array.isArray(data) ? data : []).filter(project =>
          project?.id && (!project.client_brain_id || project.client_brain_id === clientId)
        );
        setProjects(available);
      })
      .catch(() => { if (!cancelled) toast.error(t('clientWorkspaceRecovery.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, clientId, showPicker, t]);

  if (!clientId || !workspace || !needsRecovery) return null;

  const importProject = async () => {
    if (!sourceId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client-brains/${clientId}/workspace/import-project`, { project_id: sourceId });
      onRecovered?.(data);
      if (data.imported?.length) {
        toast.success(t('clientWorkspaceRecovery.success'));
        setShowPicker(false);
      } else {
        toast(t('clientWorkspaceRecovery.nothingNew'));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail?.message : detail) || t('clientWorkspaceRecovery.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl p-3 text-xs border border-[var(--border-color)]" data-testid="client-workspace-recovery">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold">{t('clientWorkspaceRecovery.title')}</p>
        <ContextHelpButton articleId="dashboard-clients" label={t('clientWorkspaceRecovery.title')} />
      </div>
      <p className="text-[var(--text-muted)] mt-1">{t('clientWorkspaceRecovery.hint')}</p>
      {!showPicker ? (
        <button type="button" className="btn-ghost mt-3" onClick={() => setShowPicker(true)} data-testid="workspace-recovery-open">
          {t('clientWorkspaceRecovery.open')}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <label className="block font-semibold" htmlFor="client-workspace-source">{t('clientWorkspaceRecovery.selectLabel')}</label>
          <select id="client-workspace-source" className="input-dark w-full" value={sourceId}
            onChange={e => setSourceId(e.target.value)} disabled={busy || loading} data-testid="workspace-recovery-source">
            <option value="">{t('clientWorkspaceRecovery.placeholder')}</option>
            {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
          {!loading && projects.length === 0 && <p className="text-[var(--text-muted)]">{t('clientWorkspaceRecovery.noProjects')}</p>}
          <div className="flex gap-2 flex-wrap">
            <button className="btn-gradient" type="button" onClick={importProject}
              disabled={!sourceId || busy || loading} data-testid="workspace-recovery-import">
              {busy ? t('common.saving') : t('clientWorkspaceRecovery.import')}
            </button>
            <button className="btn-ghost" type="button" onClick={() => setShowPicker(false)} disabled={busy}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
